import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTeamHandles, useKnownMentionSet } from '../hooks/useTeamHandles';
import { renderTextWithMentions } from './MentionTextarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
};

/**
 * Modale de previsualisation PDF avec panneau notes internes.
 * Props:
 *  - url          : URL du PDF
 *  - title        : titre modale
 *  - onClose      : callback
 *  - downloadName : nom fichier pour bouton Telecharger
 *  - entityType   : 'analysis' | 'financing' (pour les notes)
 *  - entityId     : analysis_id ou project_id (pour les notes)
 */
export const PdfPreviewModal = ({ url, title, onClose, downloadName, entityType, entityId }) => {
  const teamHandles = useTeamHandles();
  const knownSet = useKnownMentionSet();
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState('');
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [posting, setPosting] = useState(false);
  const [showMentionMenu, setShowMentionMenu] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const textareaRef = React.useRef(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);

  // 1. PDF blob
  useEffect(() => {
    let cancelled = false;
    let currentBlobUrl = null;
    (async () => {
      try {
        const r = await axios.get(url, { responseType: 'blob' });
        if (cancelled) return;
        currentBlobUrl = window.URL.createObjectURL(r.data);
        setBlobUrl(currentBlobUrl);
      } catch (e) {
        if (cancelled) return;
        setErr(e.response?.data ? 'Erreur serveur' : e.message);
      }
    })();
    return () => {
      cancelled = true;
      if (currentBlobUrl) window.URL.revokeObjectURL(currentBlobUrl);
    };
  }, [url]);

  // 2. Current user (pour droits de suppression)
  useEffect(() => {
    axios.get(`${API}/auth/me`).then(r => {
      setCurrentUserId(r.data?.user_id);
      setCurrentUserRole(r.data?.role);
    }).catch(() => {});
  }, []);

  // 3. Notes
  const fetchNotes = React.useCallback(async () => {
    if (!entityType || !entityId) return;
    setLoadingNotes(true);
    try {
      const r = await axios.get(`${API}/pdf-notes/${entityType}/${entityId}`);
      setNotes(r.data?.notes || []);
    } catch (e) {
      // silencieux
    } finally { setLoadingNotes(false); }
  }, [entityType, entityId]);

  useEffect(() => { if (notesOpen) fetchNotes(); }, [notesOpen, fetchNotes]);

  const handleNoteChange = (e) => {
    const value = e.target.value;
    setNewNote(value);
    // Detecte si on est en train de taper une mention
    const cursor = e.target.selectionStart;
    const before = value.slice(0, cursor);
    const match = before.match(/@([a-zA-Z]*)$/);
    if (match) {
      setShowMentionMenu(true);
      setMentionFilter(match[1].toLowerCase());
    } else {
      setShowMentionMenu(false);
    }
  };

  const pickMention = (handle) => {
    if (!textareaRef.current) return;
    const ta = textareaRef.current;
    const cursor = ta.selectionStart;
    const before = newNote.slice(0, cursor).replace(/@[a-zA-Z]*$/, `@${handle} `);
    const after = newNote.slice(cursor);
    const next = before + after;
    setNewNote(next);
    setShowMentionMenu(false);
    // Repositionne le cursor apres la mention
    setTimeout(() => {
      ta.focus();
      const pos = before.length;
      ta.setSelectionRange(pos, pos);
    }, 10);
  };

  const filteredHandles = teamHandles.filter(h => h.handle.startsWith(mentionFilter));

  const addNote = async () => {
    const text = newNote.trim();
    if (!text) return;
    setPosting(true);
    try {
      const r = await axios.post(`${API}/pdf-notes/${entityType}/${entityId}`, { text });
      setNotes([r.data, ...notes]);
      setNewNote('');
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur ajout note');
    } finally { setPosting(false); }
  };

  const removeNote = async (noteId) => {
    if (!window.confirm('Supprimer cette note ?')) return;
    try {
      await axios.delete(`${API}/pdf-notes/${noteId}`);
      setNotes(notes.filter(n => n.note_id !== noteId));
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur suppression');
    }
  };

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = downloadName || 'document.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const canAnnotate = !!(entityType && entityId);
  const notesBadge = notes.length > 0 ? notes.length : null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="pdf-preview-modal" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-6xl h-[92vh] flex flex-col overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-[#003E40] text-white">
          <h3 className="font-semibold truncate flex-1" data-testid="pdf-preview-title">{title || 'Apercu du document'}</h3>
          <div className="flex items-center gap-2">
            {canAnnotate && (
              <button onClick={() => setNotesOpen(!notesOpen)}
                      className={`px-3 py-1.5 text-sm font-semibold rounded flex items-center gap-1.5 ${notesOpen ? 'bg-[#E8DCC4] text-[#1A1A1A]' : 'bg-white/10 hover:bg-white/20 text-white'}`}
                      data-testid="pdf-annotate-toggle">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                Annoter
                {notesBadge && <span className="ml-1 bg-red-500 text-white text-[10px] font-bold px-1.5 rounded-full">{notesBadge}</span>}
              </button>
            )}
            <button onClick={handleDownload} disabled={!blobUrl}
                    className="px-3 py-1.5 bg-[#E8DCC4] text-[#1A1A1A] text-sm font-semibold rounded hover:bg-[#dcc9a8] disabled:opacity-50"
                    data-testid="pdf-preview-download">
              Telecharger
            </button>
            <button onClick={onClose}
                    className="text-white/80 hover:text-white text-2xl leading-none w-8 h-8 flex items-center justify-center"
                    data-testid="pdf-preview-close">&times;</button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* PDF iframe */}
          <div className="flex-1 bg-gray-100 relative">
            {err && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-red-600 p-6">
                  <p className="font-semibold">Impossible d'afficher le PDF</p>
                  <p className="text-sm mt-2">{err}</p>
                </div>
              </div>
            )}
            {!err && !blobUrl && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex items-center gap-3 text-gray-500">
                  <span className="inline-block w-5 h-5 border-2 border-[#008A8D] border-t-transparent rounded-full animate-spin"></span>
                  Generation du PDF...
                </div>
              </div>
            )}
            {blobUrl && (
              <iframe src={blobUrl} className="w-full h-full border-0" title="Previsualisation PDF" data-testid="pdf-preview-iframe" />
            )}
          </div>

          {/* Panneau Notes internes */}
          {notesOpen && canAnnotate && (
            <aside className="w-80 border-l border-gray-200 bg-white flex flex-col" data-testid="pdf-notes-panel">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <h4 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#008A8D]" fill="currentColor" viewBox="0 0 20 20"><path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/><path d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5z" /></svg>
                  Notes internes (equipe)
                </h4>
                <p className="text-xs text-gray-500 mt-1">Non visibles par le client. Utile pour les revues avant envoi.</p>
              </div>

              <div className="p-3 border-b border-gray-100 relative">
                <textarea ref={textareaRef} value={newNote} onChange={handleNoteChange}
                          placeholder="Ajouter une note. Tapez @ pour mentionner un coequipier."
                          className="w-full border border-gray-200 rounded-lg p-2 text-sm h-20 resize-none focus:border-[#008A8D] focus:outline-none"
                          data-testid="pdf-note-input" />
                {showMentionMenu && filteredHandles.length > 0 && (
                  <div className="absolute z-10 left-3 right-3 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-auto" data-testid="mention-menu">
                    {filteredHandles.map(h => (
                      <button key={h.handle} onClick={() => pickMention(h.handle)}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-[#008A8D]/10 flex items-center gap-2"
                              data-testid={`mention-${h.handle}`}>
                        <span className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: h.color }}>
                          {h.handle.charAt(0).toUpperCase()}
                        </span>
                        <span><b>@{h.handle}</b> <span className="text-gray-400 text-xs">- {h.name}</span></span>
                      </button>
                    ))}
                  </div>
                )}
                <button onClick={addNote} disabled={posting || !newNote.trim()}
                        className="mt-2 w-full px-3 py-1.5 bg-[#008A8D] text-white text-sm font-semibold rounded hover:bg-[#006B6D] disabled:opacity-50"
                        data-testid="pdf-note-add">
                  {posting ? 'Ajout...' : '+ Ajouter la note'}
                </button>
                <p className="text-[10px] text-gray-400 mt-1">Astuce : tapez @ pour mentionner un coequipier (email + notification automatique).</p>
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {loadingNotes && <p className="text-xs text-gray-400 italic">Chargement...</p>}
                {!loadingNotes && notes.length === 0 && (
                  <p className="text-xs text-gray-400 italic text-center mt-4">Aucune note pour le moment.</p>
                )}
                {notes.map(n => {
                  const canDelete = n.author_id === currentUserId || ['admin', 'ceo'].includes(currentUserRole);
                  return (
                    <div key={n.note_id} className="p-3 bg-[#E8DCC4]/20 border border-[#E8DCC4]/40 rounded-lg" data-testid={`pdf-note-${n.note_id}`}>
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="text-xs">
                          <p className="font-semibold text-gray-800">{n.author_name}</p>
                          <p className="text-gray-400">{formatDate(n.created_at)}</p>
                        </div>
                        {canDelete && (
                          <button onClick={() => removeNote(n.note_id)}
                                  className="text-red-400 hover:text-red-600 text-xs"
                                  data-testid={`pdf-note-delete-${n.note_id}`}>
                            Supprimer
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{renderTextWithMentions(n.text, knownSet)}</p>
                    </div>
                  );
                })}
              </div>
            </aside>
          )}
        </div>
      </div>
    </div>
  );
};

export default PdfPreviewModal;
